
import { Json } from '@/integrations/supabase/types';
import { Product, Connection } from '@/types';

export const parseProducts = (products: Json): Product[] => {
  if (!products) return [];
  
  if (typeof products === 'string') {
    try {
      const parsed = JSON.parse(products);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  
  if (Array.isArray(products)) {
    return (products as unknown as Product[]).map(product => ({
      id: product.id || '',
      name: product.name || '',
      description: product.description || '',
      category: product.category || ''
    }));
  }
  
  return [];
};

/**
 * Helper: for non-CSV connections, backfill missing service/connectorId
 * so Fivetran metadata is never silently dropped.
 */
const backfillConnectorFields = (conn: Connection): Connection => {
  const isCsv = conn.type === 'csv' || conn.import_type === 'csv' ||
    (conn.id && conn.id.startsWith('csv-import-')) ||
    (conn.connectorId && conn.connectorId.startsWith('csv-import-')) ||
    conn.service === 'csv';

  return {
    ...conn,
    service: conn.service || (!isCsv ? (conn.type || undefined) : undefined),
    connectorId: conn.connectorId || (!isCsv ? (conn.id || undefined) : undefined),
  };
};

function connectionFieldsForDraft(conn: Connection, fallbackId?: string): Connection {
  return {
    id: conn.id || fallbackId || '',
    name: conn.name || '',
    type: conn.type || 'other',
    status: conn.status || 'unknown',
    created_at: conn.created_at || new Date().toISOString(),
    connectionId: conn.connectionId,
    service: conn.service,
    connectorId: conn.connectorId,
    csvImports: conn.csvImports,
    import_type: conn.import_type,
    ecommerce_subtype: conn.ecommerce_subtype,
    file_name: conn.file_name,
    logoUrl: conn.logoUrl,
    description: conn.description,
    metadata: conn.metadata,
  };
}

export const parseConnections = (connections: Json): Connection[] => {
  if (!connections) return [];
  
  if (Array.isArray(connections)) {
    return (connections as unknown as Connection[]).map(conn => backfillConnectorFields(connectionFieldsForDraft(conn)));
  }
  
  if (typeof connections === 'object' && connections !== null) {
    const connectionsObj = connections as Record<string, any>;
    const connectionsArray: Connection[] = [];
    
    for (const key in connectionsObj) {
      if (Object.prototype.hasOwnProperty.call(connectionsObj, key)) {
        const conn = connectionsObj[key];
        if (conn && typeof conn === 'object') {
          connectionsArray.push(backfillConnectorFields(connectionFieldsForDraft(conn, key)));
        }
      }
    }
    return connectionsArray;
  }
  
  return [];
};
